import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";

/** A request parked behind the refresh, told whether the refresh succeeded. */
type RefreshSubscriber = (succeeded: boolean) => void; // eslint-disable-line no-unused-vars -- type-position name; the core rule reads it as a value

/** Axios has no `_retry`; the handler sets it so a second 401 is not refreshed. */
type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

type RefreshFailure = {
  config?: RetriableConfig;
  response?: { status?: number };
};

/**
 * The 401 refresh handler shared by the admin SPA and the Next.js public site.
 *
 * The in-flight flag and the subscriber queue live in this closure, one set per
 * call. Each runtime attaches once, so each bundle keeps the state the old
 * module locals held. `clearLocalAuth` stays with the caller: the Next client
 * also removes `localStorage.currentsite`, and the admin client does not
 * (SCRUM-1471).
 */
export function attachTokenRefreshInterceptor(
  client: AxiosInstance,
  clearLocalAuth: () => void,
): void {
  let isRefreshing = false;
  let refreshSubscribers: RefreshSubscriber[] = [];

  const subscribeToTokenRefresh = (cb: RefreshSubscriber) => {
    refreshSubscribers.push(cb);
  };

  const notifyTokenRefreshed = (succeeded: boolean) => {
    refreshSubscribers.forEach((cb) => cb(succeeded));
    refreshSubscribers = [];
  };

  client.interceptors.response.use(
    (response) => response,
    async (error: RefreshFailure) => {
      const originalRequest = error?.config;
      const statusCode = error?.response?.status;
      const requestUrl = originalRequest?.url || "";

      if (!originalRequest || statusCode !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      if (
        requestUrl.includes("/api/users/refresh-token") ||
        requestUrl.includes("/api/users/auth")
      ) {
        return Promise.reject(error);
      }

      if (typeof window === "undefined") {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeToTokenRefresh((succeeded) => {
            if (!succeeded) {
              reject(error);
              return;
            }
            resolve(client(originalRequest));
          });
        });
      }

      isRefreshing = true;
      try {
        await client.post("/api/users/refresh-token");
        notifyTokenRefreshed(true);
        return client(originalRequest);
      } catch (refreshError) {
        clearLocalAuth();
        notifyTokenRefreshed(false);
        if (
          typeof window !== "undefined" &&
          !window.location.pathname.startsWith("/auth")
        ) {
          window.location.href = "/auth/login";
          // Stop the current promise chain because navigation is in progress.
          return new Promise(() => {});
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    },
  );
}
